import { CommonModule, isPlatformServer } from '@angular/common';
import {
	Component,
	computed,
	effect,
	inject,
	input,
	PLATFORM_ID,
	signal,
	untracked,
	ViewEncapsulation,
} from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import { DynamicModule } from 'ng-dynamic-component';

import type {
	FiltersInterface,
	SortSelection,
} from '@valk-nx/compositions/ui-filters/src/lib/contracts/filters.interface';
import {
	DEFAULT_SORT_OPTIONS,
	FilterHeadingComponent,
} from '@valk-nx/compositions/ui-filters/src/lib/filter-heading/filter-heading';
import { FiltersComponent } from '@valk-nx/compositions/ui-filters/src/lib/filters.component';
import type { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { injectQueryParamsMap } from '@valk-nx/services/query-params';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { extractCarouselImages } from '@valk-nx/storyblok-helpers/src/lib/general/carousel-images.helper';
import { preselectedFilters } from '@valk-nx/storyblok-services/src/lib/services/filterable-list.filters';
import { FilterableListService } from '@valk-nx/storyblok-services/src/lib/services/filterable-list.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import type {
	FilterableHotel,
	FilterableHotelListData,
	FilterableListItems,
	FilterableListScenario,
	FilterablePackageBatch,
	FilterablePackageBatchListData,
} from '@valk-nx/storyblok-types/src/lib/types/filterable-list.service.interface';
import type {
	HeadingGroupSection,
	ISbStoryBlok,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

import { VdcHotels } from './lists/vdc-hotels/vdc-hotels';
import { VdcPackages } from './lists/vdc-packages/vdc-packages';
import { VdcvhiHotels } from './lists/vdcvhi-hotels/vdcvhi-hotels';
import type { DynamicContentInputs } from './filterable-list.dynamic-content';
import {
	carouselComponents,
	richTextComponents,
	subContentComponents,
	titleComponents,
	withComponentName,
} from './filterable-list.dynamic-content';
import { toBaseUrl, toFiltersInterface } from './filterable-list.helpers';
import type {
	FilterableListHotel,
	FilterableListType,
} from './filterable-list.interface';
import { LocationSearchFilter } from './location-search-filter';

const scenarioMap = {
	vdc: {
		hotels: 'vdc-hotels',
		packages: 'vdc-packages',
		events: 'vdc-events',
		rooms: 'vdc-rooms',
	},
	vdcvhi: {
		hotels: 'vdcvhi-hotels',
		packages: 'vdcvhi-packages',
		events: 'vdcvhi-events',
		rooms: 'vdcvhi-rooms',
	},
} as const satisfies Record<
	FilterableListHotel,
	Record<FilterableListType, FilterableListScenario>
>;

@Component({
	encapsulation: ViewEncapsulation.None,
	providers: [LocationSearchFilter],
	selector: 'sb-filterable-list',
	templateUrl: './filterable-list.html',
	imports: [
		CommonModule,
		DynamicModule,
		FiltersComponent,
		FilterHeadingComponent,
		TranslatePipe,
		VdcHotels,
		VdcPackages,
		VdcvhiHotels,
	],
})
export class FilterableList extends StoryblokRootDirective {
	protected readonly filterableListService = inject(FilterableListService);
	protected readonly locationSearch = inject(LocationSearchFilter);
	private readonly urlFilterParams = injectQueryParamsMap();
	private readonly metadataService = inject(MetadataService);
	private readonly platformId = inject(PLATFORM_ID);

	hotel = input.required<FilterableListHotel>();
	type = input.required<FilterableListType>();
	carousel = input.required<ISbStoryBlok[]>();
	title = input.required<HeadingGroupSection[]>();
	content = input.required<ISbStoryBlok[]>();
	subContent = input<ISbStoryBlok[]>();
	listItems = input<FilterableListItems>({});
	showPrices = input<boolean>(true);
	baseUrl = input('', { transform: toBaseUrl });

	sortOption = signal<SortSelection>('none');

	activeFilterCount = computed(() =>
		Object.values(this.filterableListService.filters()).reduce(
			(sum, values) => sum + values.length,
			0,
		),
	);

	valkBusiness = computed<boolean>(() => !!this.listItems()?.valkBusiness);

	activeScenario = computed<FilterableListScenario | null>(() => {
		const hotel = this.hotel();
		const type = this.type();

		if (!hotel || !type) return null;

		return scenarioMap[hotel]?.[type] ?? null;
	});

	private readonly activeListData = computed<
		FilterableHotelListData | FilterablePackageBatchListData | undefined
	>(() => {
		const scenario = this.activeScenario();
		if (scenario === 'vdc-hotels') {
			return this.filterableListService.vdcHotels.value();
		}
		if (scenario === 'vdc-packages') {
			return this.filterableListService.vdcPackages.value();
		}
		if (scenario === 'vdcvhi-hotels') {
			return this.filterableListService.vdcvhiHotels.value();
		}
		return undefined;
	});

	private readonly hotelList = computed<FilterableHotel[]>(() => {
		const data = this.activeListData();
		return data && 'hotels' in data ? data.hotels : [];
	});

	private readonly packageBatchList = computed<FilterablePackageBatch[]>(
		() => {
			const data = this.activeListData();
			return data && 'packageBatches' in data ? data.packageBatches : [];
		},
	);

	numberOfResults = computed(
		() => this.hotelList().length + this.packageBatchList().length,
	);

	filters = computed<FiltersInterface[]>(() => {
		const data = this.activeListData();
		return data ? toFiltersInterface(data.filters) : [];
	});

	sortOptions = computed(() => {
		// VHI hotels only support sorting by name (A-Z / Z-A)
		if (!this.showPrices()) {
			return DEFAULT_SORT_OPTIONS.filter(
				(option) => !option.value.startsWith('price'),
			);
		}

		return DEFAULT_SORT_OPTIONS;
	});

	shouldShowCarousel = computed(() => this.carousel().length > 0);

	carouselImages = computed<PhotoInterface[]>(() =>
		extractCarouselImages(this.carousel()),
	);

	carouselInputs = computed<DynamicContentInputs[]>(() =>
		withComponentName(this.carousel()),
	);

	contentInputs = computed<DynamicContentInputs[]>(() =>
		withComponentName(this.content(), { isReadMore: true }),
	);

	subContentInput = computed<DynamicContentInputs[]>(() =>
		withComponentName(this.subContent() ?? []),
	);

	carouselComponents: typeof carouselComponents = carouselComponents;
	titleComponents: typeof titleComponents = titleComponents;
	richTextComponents: typeof richTextComponents = richTextComponents;
	subContentComponents: typeof subContentComponents = subContentComponents;

	constructor() {
		super();

		effect(() => {
			this.metadataService.updateMetaDataBatch(
				{
					meta: {
						title: this.metaTitle(),
						description: this.metaDescription(),
					},
					images: this.carouselImages(),
				},
				this.robotsNoFollow(),
				this.robotsNoIndex(),
			);
		});

		effect(() => {
			this.filterableListService.scenario.set(
				this.activeScenario() ?? undefined,
			);
			// Seed the filters once per scenario: plugin preselection
			// first, URL params win. Untracked so later URL changes
			// can't overwrite the visitor's selections.
			this.filterableListService.filters.set({
				...preselectedFilters(untracked(this.listItems)),
				...untracked(this.urlFilterParams),
			});
		});

		effect(() => {
			this.filterableListService.listItems.set(this.listItems());
		});

		effect(() => {
			if (!isPlatformServer(this.platformId)) return;
			const hotels = this.hotelList();
			if (!hotels.length) return;
			untracked(() => {
				this.metadataService.setSchemaOrgHotelList(
					this.metaTitle(),
					this.metaDescription(),
					hotels,
				);
			});
		});

		effect(() => {
			if (!isPlatformServer(this.platformId)) return;
			if (this.activeScenario() !== 'vdc-packages') return;
			const packageBatches = this.packageBatchList();
			if (!packageBatches.length) return;
			untracked(() => {
				this.metadataService.setSchemaOrgFilterablePackageList(
					this.metaTitle(),
					this.metaDescription(),
					this.carouselImages(),
					packageBatches,
				);
			});
		});
	}
}
